import { Suspense, useEffect, useMemo } from 'react';
import { renderRoutes } from 'react-router-config';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles, useTheme } from '@mui/styles';
import { LinearProgress, useMediaQuery } from '@mui/material';
import AuthGuard from 'src/shared/components/AuthGuard';
import { selectUserRole } from 'src/old/actions/session/sessionSelectors';
import { selectMenuOpened } from 'src/old/actions/menu/menuSelectors';
import NavBar from '../Navbar/NavBar';
import { closeMenu, openMenu } from '../../../../old/actions';
import { selectCompany } from 'src/old/actions/company/companySelectors';
import { attachBeacon, tearDownBeacons } from 'src/shared/utils/Beacons/attachBeacons';
import { HoldingAlertsSnackbar } from 'src/modules/HoldingAlerts';
import { useIsHeroAdmin } from 'src/shared/hooks/useIsHeroAdmin';

const useStyles = makeStyles(() => ({
  container: {
    minHeight: '100vh',
    display: 'flex',
    '@media all and (-ms-high-contrast:none)': {
      height: 0 // IE11 fix
    }
  },
  content: {
    flexGrow: 1,
    maxWidth: '100%',
    overflowX: 'hidden',
    paddingLeft: ({ collapsed, mobileDevice }) => (collapsed || mobileDevice ? 90 : 256),
    transition: 'padding-left 250ms ease-in-out'
  }
}));

function Dashboard({ route }) {
  const history = useHistory();
  const theme = useTheme();
  const dispatch = useDispatch();
  const mobileDevice = useMediaQuery(theme.breakpoints.down('md'));
  const menuOpen = useSelector(selectMenuOpened);
  const classes = useStyles({ collapsed: !menuOpen, mobileDevice });
  const userRole = useSelector(selectUserRole);
  const company = useSelector(selectCompany);
  const { isHeroAdmin, isLoading: isScopeLoading, isError: isScopeError } = useIsHeroAdmin();
  // /me failing must not silently demote a Hero admin; the API enforces the real scope.
  const visibleRoutes = useMemo(
    () => (isHeroAdmin || isScopeError ? route?.routes : route?.routes?.filter(({ heroOnly }) => !heroOnly)),
    [route?.routes, isHeroAdmin, isScopeError]
  );

  useEffect(() => {
    if (!history.location.pathname.startsWith('/super')) {
      attachBeacon(company?.locale);
    }

    if (mobileDevice) {
      dispatch(closeMenu());
    } else {
      dispatch(openMenu());
    }

    return () => {
      tearDownBeacons();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileDevice, company?.locale]);

  return (
    <AuthGuard roles={['ADMIN', 'USER']}>
      <NavBar userRole={userRole} />
      <div className={classes.container}>
        <div className={classes.content}>
          {!history.location.pathname.startsWith('/super') && <HoldingAlertsSnackbar />}
          {/* Wait for the scope: rendering a Hero-only deep link before /me resolves hits the 404 catch-all. */}
          {isScopeLoading ? (
            <LinearProgress />
          ) : (
            <Suspense fallback={<LinearProgress />}>{renderRoutes(visibleRoutes)}</Suspense>
          )}
        </div>
      </div>
    </AuthGuard>
  );
}

Dashboard.propTypes = {
  route: PropTypes.object
};

export default Dashboard;
